/**
 * 대시보드 — 위에는 이 서버의 사용량 상자(CPU·메모리·디스크), 아래에는 가로 3등분 목록
 * (최근 작성·변경 / 최근 실행 / 최근 실행 오류).
 *
 * **화면 전체와 각 목록에 세로 스크롤바가 생기지 않게** 높이를 고정한다. 목록은 제 칸에 들어가는 줄 수만큼만
 * 보이고, 「더보기」를 누르면 그 칸 안에서 아래로 이어 펼쳐진다 — 이때만 그 칸에 스크롤바가 생긴다.
 * 들어가는 줄 수는 칸 높이를 재서 정한다(창 크기가 달라도 마지막 줄이 잘리지 않게).
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import store, { RootState } from '../../store';
import type { Workflow } from '../../store/project/projectSlice';
import { openWorkflow } from '../../store/workflow/openWorkflow';
import { showAlert } from '../common/AppDialog';
import WorkflowLoadNotice from '../common/WorkflowLoadNotice';
import {
  dagIdOf, fetchLiveMemory, fetchRecentRuns, fetchSystemStats, fmtDuration, LiveTaskMemory,
  RecentRuns, SystemStats, WindowRun,
} from '../../services/executionApi';
import { formatBytes, formatDateTime, formatNumber } from '../../utils/formatters';
import { usePersistentState } from '../../utils/usePersistentState';
import RunStateBadge from '../execution/RunStateBadge';

/** 목록 한 줄 높이(px) — 칸에 들어가는 줄 수를 이것으로 센다 */
const ROW_H = 52;
const STATS_EVERY_MS = 5000;
const RUNS_EVERY_MS = 30000;
const RUNS_LIMIT = 50;
const EDITED_LIMIT = 50;

type ListKey = 'edited' | 'runs' | 'failed';

interface Props {
  onOpenRunner: (workflowId: string) => void;
  onOpenDesigner: () => void;
}

const DashboardScreen: React.FC<Props> = ({ onOpenRunner, onOpenDesigner }) => {
  const { t } = useTranslation();
  const projectId = useSelector((s: RootState) => s.project.selectedProjectId);
  const project = useSelector((s: RootState) => s.project.projects.find((p) => p.id === s.project.selectedProjectId));
  const workflows = useSelector((s: RootState) => s.project.workflows);
  const wfStatus = useSelector((s: RootState) => s.project.workflowsStatus);
  const projectWorkflows = useMemo(() => workflows.filter((w) => w.projectId === projectId), [workflows, projectId]);

  // 다시 들어왔을 때 빈 상자부터 보이지 않게 마지막 값을 남겨 둔다 (화면 상태 복원 원칙)
  const [stats, setStats] = usePersistentState<SystemStats | null>('dash:stats', null);
  const [statsError, setStatsError] = useState(false);
  const [runsState, setRunsState] = usePersistentState<{ projectId: string; data: RecentRuns } | null>('dash:runs', null);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [expanded, setExpanded] = usePersistentState<Record<ListKey, boolean>>('dash:expanded', { edited: false, runs: false, failed: false });

  // ---------------------------------------------------------------- 서버 사용량
  useEffect(() => {
    let alive = true;
    const tick = () => fetchSystemStats()
      .then((s) => { if (alive) { setStats(s); setStatsError(false); } })
      .catch(() => { if (alive) setStatsError(true); });
    tick();
    const id = window.setInterval(tick, STATS_EVERY_MS);
    return () => { alive = false; window.clearInterval(id); };
  }, [setStats]);

  // ---------------------------------------------------------------- 최근 실행
  const idsKey = projectWorkflows.map((w) => w.id).sort().join(',');
  useEffect(() => {
    if (!projectId || !idsKey) return undefined;
    let alive = true;
    const ids = idsKey.split(',');
    const tick = () => fetchRecentRuns(ids, RUNS_LIMIT)
      .then((data) => { if (alive) { setRunsState({ projectId, data }); setRunsError(data.error || null); } })
      .catch((e: Error) => { if (alive) setRunsError(e.message || ''); });
    tick();
    const id = window.setInterval(tick, RUNS_EVERY_MS);
    return () => { alive = false; window.clearInterval(id); };
  }, [projectId, idsKey, setRunsState]);

  const runs = runsState && runsState.projectId === projectId ? runsState.data : null;
  const byDag = useMemo(() => new Map(projectWorkflows.map((w) => [dagIdOf(w.id), w])), [projectWorkflows]);
  const withWorkflow = (list: WindowRun[] | undefined) =>
    (list || []).map((r) => ({ run: r, wf: byDag.get(r.dag_id) })).filter((x): x is { run: WindowRun; wf: Workflow } => !!x.wf);
  const recentRuns = withWorkflow(runs?.recent);
  // 자식 실패는 부른 실행(부모) 줄 바로 아래에 묶는다 — 같은 사고가 두 줄로 따로 떠서 관계가 안 보이던 것.
  // 부모가 목록에 없으면 "부모 › 자식" 경로로 적는다
  const failedRuns = useMemo(() => {
    const list = withWorkflow(runs?.failed);
    const key = (d: string, r: string) => `${d}|${r}`;
    const present = new Set(list.map((x) => key(x.run.dag_id, x.run.dag_run_id)));
    const kids = new Map<string, typeof list>();
    const top: typeof list = [];
    list.forEach((x) => {
      const p = x.run.parent;
      if (p && present.has(key(p.dag_id, p.run_id))) {
        const k = key(p.dag_id, p.run_id);
        kids.set(k, [...(kids.get(k) || []), x]);
      } else top.push(x);
    });
    const out: Array<(typeof list)[number] & { nested?: boolean }> = [];
    const walk = (x: (typeof list)[number], nested: boolean, depth: number) => {
      out.push({ ...x, nested });
      if (depth < 5) (kids.get(key(x.run.dag_id, x.run.dag_run_id)) || []).forEach((c) => walk(c, true, depth + 1));
    };
    top.forEach((x) => walk(x, false, 0));
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runs, byDag]);
  const latestByDag = useMemo(() => new Map((runs?.recent || []).map((r) => [r.dag_id, r])), [runs]);

  // ---------------------------------------------------------------- 최근 작성·변경
  const edited = useMemo(() => [...projectWorkflows]
    .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || '') || a.name.localeCompare(b.name))
    .slice(0, EDITED_LIMIT), [projectWorkflows]);

  const openInDesigner = async (w: Workflow) => {
    try {
      await openWorkflow(store, w);
      onOpenDesigner();
    } catch (err) {
      showAlert(t('layout:explorer.loadCanvasFailed', { message: (err as Error).message || '' }));
    }
  };

  const toggle = (k: ListKey) => setExpanded((p) => ({ ...p, [k]: !p[k] }));
  const runsPlaceholder = runsError != null && !runs
    ? t('layout:dash.runsFailed', { message: runsError })
    : !runs ? t('layout:dash.loading') : null;
  const folderOf = (w: Workflow) => w.folder || t('layout:dash.rootFolder');

  return (
    <div className="h-full w-full bg-gray-50 p-4 flex flex-col gap-4 overflow-hidden">
      {/* 프로젝트 이름 — 한 줄 */}
      <div className="flex items-baseline gap-3 min-w-0 shrink-0">
        <h1 className="text-xl font-bold truncate">{project?.name}</h1>
        {project?.description && <span className="text-sm text-gray-500 truncate">{project.description}</span>}
        {/* 서버 사용량이 무엇을 잰 것인지 — Docker Desktop 에서는 가상 머신 값이라 작업관리자와 맞지 않는다 */}
        {stats?.host && (
          <span className={`ml-auto shrink-0 text-xs ${stats.host.scope === 'docker-vm' ? 'text-amber-600' : 'text-gray-500'}`}
            title={t(`layout:dash.scope.${scopeKey(stats.host.scope)}Title`)}>
            {t(`layout:dash.scope.${scopeKey(stats.host.scope)}`, { name: stats.host.name || '' })} ⓘ
          </span>
        )}
      </div>

      {/* 서버 사용량 */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 shrink-0">
        <StatBox
          label={t('layout:dash.cpu')}
          value={pct(stats?.cpu.percent)}
          percent={stats?.cpu.percent}
          detail={coresText(stats, t)}
          failed={statsError}
        />
        <StatBox
          label={t('layout:dash.memory')}
          value={pct(stats?.memory.percent)}
          percent={stats?.memory.percent}
          detail={stats?.memory.total ? t('layout:dash.memoryDetail', { total: formatBytes(stats.memory.total), available: formatBytes(stats.memory.available) }) : ''}
          failed={statsError}
        />
        <StatBox
          label={t('layout:dash.disk')}
          value={pct(stats?.disk.percent)}
          percent={stats?.disk.percent}
          detail={stats?.disk.total ? t('layout:dash.diskDetail', { total: formatBytes(stats.disk.total), used: formatBytes(stats.disk.used) }) : ''}
          failed={statsError}
        />
        <StatBox
          label={t('layout:dash.diskFree')}
          value={stats?.disk.free != null ? formatBytes(stats.disk.free) : '—'}
          detail={stats?.disk.path || ''}
          failed={statsError}
        />
        <StatBox
          label={t('layout:dash.workflowCount')}
          // 못 읽었는데 '0' 이라고 적으면 그건 거짓말이다
          value={wfStatus === 'failed' ? '—' : formatNumber(projectWorkflows.length)}
          detail={project?.name || ''}
          accent="text-purple-600"
        />
        <LiveMemoryBox systemTotal={stats?.memory.total ?? null} workflows={workflows} />
      </div>

      {/* 최근 목록 — 가로 3등분, 남은 높이를 모두 쓴다 */}
      <div className="grid grid-cols-3 gap-3 flex-1 min-h-0">
        <ListPanel
          title={t('layout:dash.recentEdited')}
          items={edited}
          expanded={expanded.edited}
          onToggle={() => toggle('edited')}
          placeholder={wfStatus !== 'ready' || !edited.length
            ? <WorkflowLoadNotice compact emptyHint={<span>{t('layout:dash.empty')}</span>} />
            : null}
          renderRow={(w) => (
            <Row
              key={w.id}
              icon={w.icon || '📋'}
              name={w.name}
              sub={folderOf(w)}
              title={t('layout:dash.openDesigner')}
              onClick={() => openInDesigner(w)}
              right={(
                <div className="flex flex-col items-end gap-0.5">
                  {!w.curVerNo && <span className="px-1.5 rounded bg-blue-100 text-blue-700 text-[0.6875rem]">{t('layout:dash.newFile')}</span>}
                  <span className="text-xs text-gray-500 tabular-nums">{formatDateTime(w.updatedAt, { seconds: false })}</span>
                </div>
              )}
            />
          )}
        />
        <ListPanel
          title={t('layout:dash.recentRuns')}
          items={recentRuns}
          expanded={expanded.runs}
          onToggle={() => toggle('runs')}
          placeholder={runsPlaceholder ?? (recentRuns.length ? null : t('layout:dash.noRuns'))}
          renderRow={({ run, wf }) => (
            <Row
              key={run.dag_id}
              icon={wf.icon || '📋'}
              name={wf.name}
              sub={runWhen(run)}
              title={t('layout:dash.openRunner')}
              onClick={() => onOpenRunner(wf.id)}
              right={<RunStateBadge state={run.state} handled={run.handled_failure} className="px-2 py-0.5 rounded-full text-xs font-semibold" />}
            />
          )}
        />
        <ListPanel
          title={t('layout:dash.recentFailed')}
          items={failedRuns}
          expanded={expanded.failed}
          onToggle={() => toggle('failed')}
          tone="danger"
          placeholder={runsPlaceholder ?? (failedRuns.length ? null : t('layout:dash.noFailed'))}
          renderRow={({ run, wf, nested }) => {
            // 이 오류 뒤에 다시 돌려 성공했으면 알려 준다 — 지금도 고장인지가 먼저 궁금하다
            const latest = latestByDag.get(run.dag_id);
            const recovered = latest && latest.dag_run_id !== run.dag_run_id && latest.state === 'success';
            const ff = run.first_failure;
            const cause = ff ? `${ff.task_id}: ${(ff.message || '').split('\n')[0]}` : '';
            // 부모가 목록에 없으면 "부모 › 자식" 으로 — 누가 불러서 난 오류인지
            const path = !nested && run.parent ? `${run.parent.name || run.parent.dag_id} › ` : '';
            return (
              <Row
                key={`${run.dag_id}|${run.dag_run_id}`}
                icon={nested ? '↳' : (wf.icon || '📋')}
                name={`${path}${wf.name}`}
                sub={cause ? `${runWhen(run)} · ${cause}` : runWhen(run)}
                title={ff?.message ? `${cause}\n\n${t('layout:dash.openRunner')}` : t('layout:dash.openRunner')}
                onClick={() => onOpenRunner(wf.id)}
                indent={nested}
                right={recovered
                  ? <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600" title={t('layout:dash.recoveredTitle')}>{t('layout:dash.recovered')}</span>
                  : <RunStateBadge state={run.state} handled={run.handled_failure} className="px-2 py-0.5 rounded-full text-xs font-semibold" />}
              />
            );
          }}
        />
      </div>
    </div>
  );
};

/** 코어 수 — 물리·논리를 나눠 적는다 (6코어·12스레드가 「12코어」 로 보이지 않게) */
const coresText = (stats: SystemStats | null, t: (k: string, o?: any) => string): string => {
  const logical = stats?.cpu.logical ?? stats?.cpu.cores;
  const physical = stats?.cpu.physical;
  if (!logical) return '';
  return physical && physical !== logical
    ? t('layout:dash.coresBoth', { physical, logical })
    : t('layout:dash.coresLogical', { n: logical });
};

const scopeKey = (s: string) => (s === 'docker-vm' ? 'dockerVm' : s === 'container' ? 'container' : 'host');

const pct = (v: number | null | undefined) => (v == null ? '—' : `${formatNumber(v, { maximumFractionDigits: v < 10 ? 1 : 0 })}%`);

/** 실행 한 줄의 둘째 줄 — 시작 시각 · 소요 시간 (아직 도는 중이면 시작 시각만) */
const runWhen = (r: WindowRun) => {
  const at = formatDateTime(r.start_date || r.execution_date, { seconds: false });
  const took = fmtDuration(r.start_date, r.end_date);
  return took === '-' ? at : `${at} · ${took}`;
};

// ---------------------------------------------------------------- 사용량 상자

const StatBox: React.FC<{
  label: string; value: string; detail: string; percent?: number | null; failed?: boolean; accent?: string;
  /** 누르면 자세히 — 주면 상자가 버튼이 된다 */
  onClick?: () => void; pressed?: boolean; title?: string;
}> = ({ label, value, detail, percent, failed, accent, onClick, pressed, title }) => {
  const { t } = useTranslation();
  const bar = percent == null ? null : percent >= 90 ? 'bg-red-500' : percent >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      {...(onClick ? { type: 'button' as const, onClick, 'aria-expanded': !!pressed, title } : {})}
      className={`w-full text-left bg-white border rounded-lg shadow-sm px-4 py-3 h-[5.5rem] flex flex-col justify-between min-w-0 ${onClick ? `hover:border-blue-400 ${pressed ? 'border-blue-500' : ''}` : ''}`}
    >
      <div className="flex items-baseline justify-between gap-2 min-w-0">
        <span className="text-xs font-medium text-gray-500 truncate">{label}</span>
        <span className={`text-2xl font-bold tabular-nums ${accent || 'text-gray-800'}`}>{value}</span>
      </div>
      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
        {bar && <div className={`h-full rounded-full transition-all duration-500 ${bar}`} style={{ width: `${Math.min(100, Math.max(0, percent || 0))}%` }} />}
      </div>
      <div className="text-[0.6875rem] text-gray-500 truncate" title={detail}>
        {failed ? t('layout:dash.statsFailed') : detail || ' '}
      </div>
    </Tag>
  );
};

// ---------------------------------------------------------------- 실행 중 메모리

const LIVE_EVERY_MS = 3000;

/**
 * 지금 도는 노드 실행 프로세스들의 메모리 합 — 누르면 아래로 목록(워크플로·노드·지금·최대·늘어난 양·경과).
 * Airflow 태스크가 스스로 적는 기록(backend task_memory)을 읽는다. 스탠드얼론은 실행이 한 프로세스 안의
 * 스레드라 나눠 잴 수 없어 그렇게 알린다.
 */
const LiveMemoryBox: React.FC<{ systemTotal: number | null; workflows: Workflow[] }> = ({ systemTotal, workflows }) => {
  const { t } = useTranslation();
  const [data, setData] = usePersistentState<{ supported: boolean; items: LiveTaskMemory[]; total: number | null } | null>('dash:liveMem', null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    const tick = () => fetchLiveMemory()
      .then((d) => { if (alive) { setData(d); setFailed(false); } })
      .catch(() => { if (alive) setFailed(true); });
    tick();
    const id = window.setInterval(tick, LIVE_EVERY_MS);
    return () => { alive = false; window.clearInterval(id); };
  }, [setData]);

  // 바깥을 누르거나 Esc 로 닫는다
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const nameOf = useMemo(() => {
    const m = new Map(workflows.map((w) => [dagIdOf(w.id), w.name]));
    return (dagId: string) => m.get(dagId) || dagId;
  }, [workflows]);

  const unsupported = !!data && !data.supported;
  const items = data?.items || [];
  const total = data?.total ?? null;
  return (
    <div ref={boxRef} className="relative min-w-0">
      <StatBox
        label={t('layout:dash.liveMem')}
        value={unsupported || total == null ? '—' : formatBytes(total)}
        percent={!unsupported && total != null && systemTotal ? (total / systemTotal) * 100 : null}
        detail={unsupported ? t('layout:dash.liveMemUnsupported') : t('layout:dash.liveMemCount', { n: formatNumber(items.length) })}
        failed={failed}
        onClick={unsupported ? undefined : () => setOpen((v) => !v)}
        pressed={open}
        title={t('layout:dash.liveMemTitle')}
      />
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-[34rem] max-w-[90vw] bg-white border rounded-lg shadow-lg">
          <div className="px-3 h-9 flex items-center border-b text-xs font-semibold text-gray-700">
            {t('layout:dash.liveMemHeader')}
            <span className="ml-auto font-normal text-gray-400">{t('layout:dash.liveMemEvery', { n: LIVE_EVERY_MS / 1000 })}</span>
          </div>
          {items.length === 0
            ? <div className="p-4 text-sm text-gray-500">{t('layout:dash.liveMemEmpty')}</div>
            : (
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50 text-gray-500">
                    <tr className="text-left">
                      <th className="px-3 py-1 font-medium">{t('layout:dash.liveMemWorkflow')}</th>
                      <th className="px-2 py-1 font-medium text-right">{t('layout:dash.liveMemNow')}</th>
                      <th className="px-2 py-1 font-medium text-right">{t('layout:dash.liveMemPeak')}</th>
                      <th className="px-2 py-1 font-medium text-right">{t('layout:dash.liveMemGrew')}</th>
                      <th className="px-3 py-1 font-medium text-right">{t('layout:dash.liveMemElapsed')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((i) => (
                      <tr key={`${i.dag_id}|${i.run_id}|${i.task_id}`} className="border-t border-gray-100">
                        <td className="px-3 py-1.5 max-w-[14rem]">
                          <div className="truncate text-gray-800" title={i.dag_id}>{nameOf(i.dag_id)}</div>
                          <div className="truncate text-gray-400 font-mono" title={`${i.run_id} · pid ${i.pid ?? '-'}`}>{i.task_id}</div>
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-gray-800">{formatBytes(i.rss)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-gray-600">{formatBytes(i.peak)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-gray-600">{i.peak != null && i.base != null ? formatBytes(Math.max(0, i.peak - i.base)) : '-'}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">{fmtDuration(i.start, i.updated)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          <div className="px-3 py-1.5 border-t text-[0.6875rem] text-gray-400">{t('layout:dash.liveMemNote')}</div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- 목록 칸

function ListPanel<T>({ title, items, renderRow, expanded, onToggle, placeholder, tone }: {
  title: string;
  items: T[];
  renderRow: (item: T) => React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  /** 목록 대신 보여 줄 것(불러오는 중·실패·없음). null 이면 목록을 그린다 */
  placeholder: React.ReactNode;
  tone?: 'danger';
}) {
  const { t } = useTranslation();
  const bodyRef = useRef<HTMLDivElement>(null);
  const fit = useFitCount(bodyRef);
  const more = Math.max(0, items.length - fit);
  const open = expanded && more > 0;
  const shown = open ? items : items.slice(0, fit);

  // 펼치면 새로 나온 줄이 보이게 — 접혀 있을 때의 마지막 줄을 맨 위로 올린다
  const prevOpen = useRef(open);
  useEffect(() => {
    const el = bodyRef.current;
    if (el && open && !prevOpen.current) el.scrollTo({ top: Math.max(0, (fit - 1) * ROW_H), behavior: 'smooth' });
    if (el && !open) el.scrollTop = 0;
    prevOpen.current = open;
  }, [open, fit]);

  return (
    <section className="flex flex-col min-h-0 min-w-0 bg-white border rounded-lg shadow-sm overflow-hidden">
      <header className={`h-11 shrink-0 px-4 flex items-center gap-2 border-b ${tone === 'danger' ? 'bg-red-50/60' : 'bg-gray-50/60'}`}>
        <span className={`font-semibold text-sm ${tone === 'danger' ? 'text-red-700' : 'text-gray-800'}`}>{title}</span>
        {!placeholder && <span className="text-xs text-gray-400 tabular-nums">{formatNumber(items.length)}</span>}
      </header>
      <div ref={bodyRef} className={`flex-1 min-h-0 ${open ? 'overflow-y-auto' : 'overflow-hidden'}`}>
        {placeholder
          ? <div className="p-4 text-sm text-gray-500">{placeholder}</div>
          : <ul>{shown.map(renderRow)}</ul>}
      </div>
      <footer className="h-9 shrink-0 border-t flex items-center justify-center">
        {!placeholder && more > 0 && (
          <button type="button" onClick={onToggle} className="text-xs text-blue-600 hover:text-blue-800 hover:underline px-3 py-1">
            {expanded ? t('layout:dash.less') : t('layout:dash.more', { n: formatNumber(more) })}
          </button>
        )}
      </footer>
    </section>
  );
}

/** 칸 높이에 온전히 들어가는 줄 수 — 창 크기가 바뀌면 다시 잰다 */
function useFitCount(ref: React.RefObject<HTMLElement>): number {
  const [fit, setFit] = useState(5);
  const measure = useCallback(() => {
    const h = ref.current?.clientHeight || 0;
    if (h > 0) setFit(Math.max(1, Math.floor(h / ROW_H)));
  }, [ref]);
  useLayoutEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, measure]);
  return fit;
}

const Row: React.FC<{
  icon: string; name: string; sub: string; right: React.ReactNode; title: string; onClick: () => void;
  /** 위 줄(부모 실행)에 딸린 줄 — 들여 쓴다 */
  indent?: boolean;
}> = ({ icon, name, sub, right, title, onClick, indent }) => (
  <li style={{ height: ROW_H }} className="border-b last:border-b-0 border-gray-100">
    <button type="button" onClick={onClick} title={title}
      className={`w-full h-full pr-4 flex items-center gap-3 text-left hover:bg-gray-50 ${indent ? 'pl-9' : 'pl-4'}`}>
      <span className="text-lg shrink-0">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-gray-800 truncate">{name}</span>
        <span className="block text-xs text-gray-500 truncate">{sub}</span>
      </span>
      <span className="shrink-0">{right}</span>
    </button>
  </li>
);

export default DashboardScreen;
